import {
  FavouriteItem,
  FavouriteType,
  FAVOURITES_STORAGE_KEY_PREFIX,
  MAX_FAVOURITES
} from '@/interfaces/Favourite.interface';

export type FavouritesChangeListener = (favourites: FavouriteItem[]) => void;

const listeners = new Set<FavouritesChangeListener>();

export function favouritesStorageKey(userId: string | number): string {
  return `${FAVOURITES_STORAGE_KEY_PREFIX}:${userId}`;
}

export function buildFavouriteId(
  type: FavouriteType,
  stableKey: string | number
): string {
  return `${type}:${stableKey}`;
}

export function createFavouriteItem(input: {
  type: FavouriteType;
  stableKey: string | number;
  title: string;
  path: string;
  createdAt?: number;
}): FavouriteItem {
  return {
    id: buildFavouriteId(input.type, input.stableKey),
    type: input.type,
    title: input.title,
    path: input.path,
    createdAt: input.createdAt ?? Date.now()
  };
}

function isFavouriteItem(value: unknown): value is FavouriteItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === 'string' &&
    typeof item.type === 'string' &&
    typeof item.title === 'string' &&
    typeof item.path === 'string' &&
    typeof item.createdAt === 'number'
  );
}

export function parseFavourites(raw: string | null): FavouriteItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isFavouriteItem)
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, MAX_FAVOURITES);
  } catch {
    return [];
  }
}

export function readFavourites(userId: string | number | null | undefined): FavouriteItem[] {
  if (userId == null || typeof window === 'undefined') return [];
  try {
    return parseFavourites(
      window.localStorage.getItem(favouritesStorageKey(userId))
    );
  } catch {
    return [];
  }
}

export function writeFavourites(
  userId: string | number | null | undefined,
  favourites: FavouriteItem[]
): FavouriteItem[] {
  if (userId == null || typeof window === 'undefined') return [];
  const next = [...favourites]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_FAVOURITES);
  try {
    window.localStorage.setItem(
      favouritesStorageKey(userId),
      JSON.stringify(next)
    );
  } catch {
    // ignore quota / private mode failures
  }
  notifyFavouritesListeners(next);
  return next;
}

export function subscribeFavourites(
  listener: FavouritesChangeListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyFavouritesListeners(favourites: FavouriteItem[]): void {
  listeners.forEach((listener) => listener(favourites));
}

export type ToggleFavouriteResult =
  | { ok: true; favourites: FavouriteItem[]; added: boolean }
  | { ok: false; reason: 'at_cap'; favourites: FavouriteItem[] };

export function toggleFavouriteInList(
  current: FavouriteItem[],
  item: Omit<FavouriteItem, 'createdAt'> & { createdAt?: number }
): ToggleFavouriteResult {
  const existingIndex = current.findIndex((fav) => fav.id === item.id);
  if (existingIndex !== -1) {
    const favourites = current.filter((fav) => fav.id !== item.id);
    return { ok: true, favourites, added: false };
  }
  if (current.length >= MAX_FAVOURITES) {
    return { ok: false, reason: 'at_cap', favourites: current };
  }
  const favourites = [
    {
      ...item,
      createdAt: item.createdAt ?? Date.now()
    },
    ...current
  ].sort((a, b) => b.createdAt - a.createdAt);
  return { ok: true, favourites, added: true };
}
